/**
 * AUTH-001 / AUTH-002 — the ONE way to delete Supabase auth state in the browser.
 *
 * The browser client writes its session cookie with `SameSite=None; Secure; Partitioned`
 * (src/lib/supabase/client.tsx). Chrome keeps partitioned and unpartitioned cookies in
 * separate jars, so a deletion WITHOUT `Partitioned` leaves the partitioned session
 * cookie in place. Before AUTH-002 the client's own delete omitted it, so every
 * `signOut()` left the session cookie behind: middleware saw it and bounced `/login`
 * back to the dashboard with a revoked session, and once the token expired the library
 * re-read the undeletable cookie and retried the dead refresh token at network speed
 * (the 2026-10-10 flood, ~17,000 `/token` calls from one tab). See the AUTH-002 Phase 0
 * report.
 *
 * So every deletion writes every variant the app has ever used, and every sign-out path
 * calls `clearStoredTokens()` — never an inlined copy.
 */

/** Cookies and storage keys that hold Supabase auth state. */
function isAuthCookieName(name: string): boolean {
  return name.startsWith('sb-');
}

function isAuthStorageKey(key: string): boolean {
  return (
    key.startsWith('sb-') ||
    key.startsWith('sb_') ||
    key.startsWith('supabase.auth') ||
    key.startsWith('supabase-')
  );
}

/**
 * Expire one cookie in every attribute combination the app (or the server routes) may
 * have written it with: partitioned and unpartitioned, Lax and None.
 */
export function expireCookie(name: string, doc: Pick<Document, 'cookie'> = document): void {
  doc.cookie = `${name}=; Path=/; Max-Age=0`;
  doc.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
  doc.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
  doc.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure; Partitioned`;
}

/**
 * The same four deletions as `Set-Cookie` header values, for server routes that clear
 * the session on a redirect response (`/api/signout`, `/auth/signout`).
 */
export function expiredCookieHeaders(name: string): string[] {
  const base = `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  return [
    base,
    `${base}; SameSite=Lax`,
    `${base}; SameSite=None; Secure`,
    `${base}; SameSite=None; Secure; Partitioned`,
  ];
}

export function isSupabaseAuthCookie(name: string): boolean {
  return isAuthCookieName(name);
}

/**
 * Remove every Supabase auth cookie (all variants, including chunked `.0`/`.1` names)
 * and every auth key in localStorage (the embedded/iframe fallback stores the session
 * there as `sb_<name>`).
 */
export function clearStoredTokens(): void {
  if (typeof document !== 'undefined') {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0].trim();
      if (name && isAuthCookieName(name)) expireCookie(name);
    });
  }
  try {
    Object.keys(localStorage).forEach((k) => {
      if (isAuthStorageKey(k)) localStorage.removeItem(k);
    });
  } catch {
    // storage unavailable (private mode, sandboxed iframe) — nothing to clear
  }
}
